import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorToast } from '../request-options.ts';
import { assertResolutionId } from './loan-verification.ts';

const resolutionsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'resolutions' },
  op: 'query',
  resourcePath: 'api/v2/resolutions',
};

/** The member's answer for one held loan. */
export interface LoanIdentityDecision {
  holdId: string;
  /** The existing loan the held loan replaces; null adds it as a new loan. */
  existingLoanId: string | null;
}

/** What happened to one held loan in a recorded answer. */
export interface LoanIdentityOutcome {
  holdId: string;
  /** The existing loan the answer replaces; null for a new loan, which the next reflection writes. */
  loanId: string | null;
  verdict: 'same' | 'different';
  /** False when this exact answer was already recorded — a replay, not a second write. */
  recorded: boolean;
}

/** The command envelope's `result` for a recorded loan verification. */
export interface LoanIdentityAnswerResult {
  /** False when every decision repeated an answer already recorded. */
  recorded: boolean;
  decisions: LoanIdentityOutcome[];
}

/** One refused decision, read off a 422's JSON pointer. */
export interface LoanIdentityFieldError {
  /** Position in the submitted `decisions`, or null for a refusal of the whole body. */
  index: number | null;
  detail: string;
}

/** What a refused answer said, so the page can explain it in place. */
export interface LoanIdentityRefusal {
  status: number | null;
  message: string | null;
  fieldErrors: LoanIdentityFieldError[];
  /** Held loans a later reflection already wrote; the server closed their questions (409). */
  discardedHoldIds: string[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

const DECISION_POINTER = /^\/decisions\/(\d+)(?:\/|$)/;

function fieldError(raw: unknown): LoanIdentityFieldError {
  const error = asRecord(raw);
  const pointer = asRecord(error['source'])['pointer'];
  const match = typeof pointer === 'string' ? DECISION_POINTER.exec(pointer) : null;
  return {
    index: match ? Number(match[1]) : null,
    detail: typeof error['detail'] === 'string' ? error['detail'] : '',
  };
}

/** Reads a thrown answer error; a body that is not a command envelope yields empty lists, never a guess. */
export function readLoanIdentityRefusal(error: unknown): LoanIdentityRefusal {
  const raw = asRecord(error);
  const status = raw['status'] ?? asRecord(raw['response'])['status'];
  const content = asRecord(raw['content']);
  const data = asRecord(content['data']);
  const attributes = asRecord(data['attributes']);
  const commandErrors = attributes['commandErrors'];
  const discarded = asRecord(attributes['result'])['discardedHoldIds'];

  return {
    status: typeof status === 'number' ? status : null,
    message: typeof attributes['message'] === 'string' ? attributes['message'] : null,
    fieldErrors: Array.isArray(commandErrors) ? commandErrors.map(fieldError) : [],
    discardedHoldIds: Array.isArray(discarded)
      ? discarded.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

/**
 * POST /api/v2/resolutions/:id/answer — the signed-in member answers the loan identity
 * questions one upload raised, one decision per held loan: body `{ decisions: [{ hold_id,
 * existing_loan_id }] }` in snake_case, string ids, `existing_loan_id: null` for a new loan.
 *
 * Records the answer in full or not at all; the document's next reflection applies it, and there is
 * no correction afterwards. Read the result
 * with `parseCommandResult<LoanIdentityAnswerResult>` and a refusal with
 * {@link readLoanIdentityRefusal}. The failed-request toast is skipped because the page shows
 * every refusal in place, per field; an expired session still logs out.
 *
 * @throws Error before any request when the id is not numeric, see `assertResolutionId`
 * @throws 404 when the question, its document or a named held loan is not the caller's to answer
 * @throws 409 when a different answer was already recorded, the account has closed, or a held loan
 *   was already written from the document (`discardedHoldIds`) — the caller acted on state it has
 *   not seen, so re-read the page
 * @throws 422 when a decision names a loan off the account or breaks strict 1:1 — one pointer per
 *   refused decision
 */
export function answerLoanIdentityQuestionRequest(
  resolutionId: string,
  decisions: LoanIdentityDecision[]
): RequestInfo {
  assertResolutionId(resolutionId, 'answerLoanIdentityQuestionRequest');
  return {
    url: `${buildBaseURL(resolutionsUrlOptions)}/${encodeURIComponent(resolutionId)}/answer`,
    method: 'POST',
    body: JSON.stringify({
      decisions: decisions.map((decision) => ({
        hold_id: decision.holdId,
        existing_loan_id: decision.existingLoanId,
      })),
    }),
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorToast]: true },
  };
}
